import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FieldsetContainer from 'components/blocks/Delta/Inputs/Containers/FieldsetContainer';
import Loader from 'components/blocks/Loader/Loader';
import AccordionForm from 'components/blocks/ManagementNewForms/Accordion/AccordionForm';
import FormInputCuentaBancaria from 'components/blocks/ManagementNewForms/Inputs/FormInputCuentaBancaria';
import FormInputText from 'components/blocks/ManagementNewForms/Inputs/FormInputTexto';
import ErrorDisplay from 'components/blocks/ManagementNewForms/ErrorManagement/ErrorPantalla';
import FormObservaciones from 'components/blocks/ManagementNewForms/Inputs/FormObservaciones';
import FormBotonSubmit from 'components/blocks/ManagementNewForms/Inputs/FormInputSubmit';
import FormDatosPersonales from 'components/blocks/ManagementNewForms/ButtonGroups/FormDatosPersonales';
import FormInputDropDown from 'components/blocks/ManagementNewForms/Inputs/FormInputDropDown';
import FormInfoCreateUpdate from 'components/blocks/ManagementNewForms/Inputs/FormInfoCreateUpdate';
import FormCalculadoraKilometraje, { adaptCalculadoraKilometraje, exportCalculadoraKilometraje } from 'components/blocks/ManagementNewForms/Inputs/FormCalculadoraKilometraje';
import CalculadoraGastosOtrosVehiculos, { adaptGastosOtrosVehiculos, exportGastosOtrosVehiculos } from 'components/blocks/ManagementNewForms/Inputs/CalculadoraGastosOtrosVehiculos';
import useDecode from 'hooks/useDecode';
import useCreateSolicitudGastosTransporte from 'services/record/SolicitudGastosTransporte/useCreateSolicitudGastosTransporte';
import useUpdateSolicitudGastosTransporteByID from 'services/record/SolicitudGastosTransporte/useUpdateSolicitudGastosTransporte';

interface SolicitudGastosTransporteProps {
    info: boolean;
    dataUser: any;
    onSuccess?: () => void;
}

const MAX_SIZE_MB = 30;

const SolicitudGastosTransporte: React.FC<SolicitudGastosTransporteProps> = ({ info, dataUser, onSuccess }) => {
    const formMethods = useForm();
    const { register, handleSubmit, reset } = formMethods;
    const tokenResponse = useDecode("token");

    const { createSolicitudGastosTransporte, loadingCreateSolicitudGastosTransporte, successCreateSolicitudGastosTransporte, errorCreateSolicitudGastosTransporte } = useCreateSolicitudGastosTransporte();
    const { updateSolicitudGastosTransporteById, loadingUpdateSolicitudGastosTransporteById, successUpdateSolicitudGastosTransporteById, errorUpdateSolicitudGastosTransporteById } = useUpdateSolicitudGastosTransporteByID();

    const [totalSize, setTotalSize] = useState(0);
    const [datosAPI, setDatosAPI] = useState(dataUser);
    const [expedienteArray, setExpedienteArray] = useState([]);
    const [loader, setLoader] = useState(false);
    const [errorObservaciones, setErrorObservaciones] = useState(false);
    const [bloquearEnvio, setBloquearEnvio] = useState<boolean>(false);
    const [expedienteBloqueadoLabel, setExpedienteBloqueadoLabel] = useState("");
    const [expedienteNoEncontradoError, setExpedienteNoEncontradoError] = useState<string | null>(null);
    const requiredViajesTransporte = 'Debe añadir al menos un viaje o gasto de transporte';

    const parseNumberOrNull = (value: any) => {
        if (value === null || value === undefined || value === "") return null;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    };
    const formatIban = (value: any) => {
        if (value === null || value === undefined || value === "") return null;
        return String(value).replace(/(.{4})(.{4})(.{4})(.{2})/, '$1-$2-$3-$4-');
    };
    const getPrestacionesCP = (data: any) => {
        const prestaciones = data?.PrestacionCP ?? [];
        return Array.isArray(prestaciones) ? prestaciones : [];
    };
    const getExpedienteExternalId = (item: any) => item?.IdExternalPrestacion ?? item?.ExpedienteId ?? null;
    const getPrestacionId = (item: any) => item?.IdPortalPrestacion ?? item?.IdPrestacion ?? item?.PrestacionId ?? null;
    const getExpedienteLabel = (item: any) => `${item?.CodigoExpediente ?? ''} - ${getExpedienteExternalId(item) ?? ''}`;
    const adaptApiResponseToDefaultValues = (data: any) => {
        if (!data) {
            return;
        }

        const prestacionesCP = getPrestacionesCP(data);
        const expedienteIdByOrigen = data?.ExpedienteIdByOrigen ?? null;
        const lockExpedienteByOrigenActivo = info === true && expedienteIdByOrigen !== null && expedienteIdByOrigen !== undefined && expedienteIdByOrigen !== "";
        let expedienteBloqueado = null;

        if (prestacionesCP.length > 0) {
            const expedientesAdaptados = prestacionesCP.map(item => ({
                titulo: getExpedienteLabel(item),
                value: getPrestacionId(item),
            }));

            setExpedienteArray(expedientesAdaptados);

            if (lockExpedienteByOrigenActivo) {
                expedienteBloqueado = prestacionesCP.find(
                    item => String(getExpedienteExternalId(item) ?? "") === String(expedienteIdByOrigen)
                ) || null;
            }
        } else {
            setExpedienteArray([]);
        }

        if (lockExpedienteByOrigenActivo && expedienteBloqueado) {
            setExpedienteBloqueadoLabel(getExpedienteLabel(expedienteBloqueado));
            setExpedienteNoEncontradoError(null);
        } else if (lockExpedienteByOrigenActivo) {
            setExpedienteBloqueadoLabel("");
            setExpedienteNoEncontradoError(`No se encuentra el ExpedienteId ${String(expedienteIdByOrigen)} entre los Expedientes del Usuario.`);
            return;
        } else {
            setExpedienteBloqueadoLabel("");
            setExpedienteNoEncontradoError(null);
        }

        const rawIban = data?.DatosBancarios?.SolicitanteIBAN ?? data?.IBAN ?? data?.NumeroCuentaConIBAN ?? null;
        const estadoFormulario = data?.EstadoFormulario ?? null;

        setErrorObservaciones(estadoFormulario === 2);
        setTotalSize(0);

        reset({
            DatosPersonales: {
                SolicitanteNombre: data?.DatosPersonales?.Nombre ?? null,
                SolicitanteApellido1: data?.DatosPersonales?.Apellido1 ?? null,
                SolicitanteApellido2: data?.DatosPersonales?.Apellido2 ?? null,
                SolicitanteIPF: data?.DatosPersonales?.IPF ?? null,
                SolicitanteIPFTipo: data?.DatosPersonales?.IPFTipo?.toString() ?? null,
                SolicitanteTelefono: data?.DatosPersonales?.Telefono ?? null,
                SolicitanteFijo: data?.DatosPersonales?.Fijo ?? null,
                SolicitanteDomicilio: data?.DatosPersonales?.Domicilio ?? null,
                SolicitanteCodigoPostal: data?.DatosPersonales?.CodigoPostal ?? null,
                SolicitanteProvincia: data?.DatosPersonales?.CodigoProvincia ?? null,
                SolicitanteProvinciaDescripcion: data?.DatosPersonales?.ProvinciaDescripcion ?? data?.DatosPersonales?.Provincia ?? null,
                SolicitanteComunidadCodigo: data?.DatosPersonales?.ComunidadCodigo ?? data?.DatosPersonales?.CodigoLocalidad ?? null,
                SolicitanteComunidadCodigoDescripcion: data?.DatosPersonales?.ComunidadCodigoDescripcion ?? data?.DatosPersonales?.LocalidadDescripcion ?? null,
            },
            TipoGastos: {
                TipoGasto: 'Transporte',
                ExpedienteGasto: info
                    ? (lockExpedienteByOrigenActivo ? (getPrestacionId(expedienteBloqueado) ?? null) : null)
                    : `${data?.CodigoExpediente ?? ''} - ${data?.ExpedienteId ?? ''}`,
                ViajesVehiculoPropio: adaptCalculadoraKilometraje(data?.DatosVehiculoPropio ?? []),
                ViajesOtrosVehiculos: adaptGastosOtrosVehiculos(data?.DatosOtrosTransportes ?? []),
            },
            DatosBancarios: {
                SolicitanteIBAN: rawIban ? String(rawIban).replace(/-/g, '') : null,
            },
            Acepto: data?.Acepto ?? null,
        });
    };

    useEffect(() => {
        setDatosAPI(dataUser);
        adaptApiResponseToDefaultValues(dataUser);
    }, [dataUser]);

    useEffect(() => {
        setBloquearEnvio(totalSize > MAX_SIZE_MB || Boolean(expedienteNoEncontradoError));
    }, [totalSize, expedienteNoEncontradoError]);

    const onSubmit = async (data: any) => {
        setLoader(true);

        if (expedienteNoEncontradoError) {
            setLoader(false);
            return;
        }

        const prestacionesCP = getPrestacionesCP(datosAPI);
        const expedienteIdByOrigen = datosAPI?.ExpedienteIdByOrigen ?? null;
        const lockExpedienteByOrigenActivo = info === true && expedienteIdByOrigen !== null && expedienteIdByOrigen !== undefined && expedienteIdByOrigen !== "";
        const expedienteSeleccionado = lockExpedienteByOrigenActivo
            ? prestacionesCP.find(item => String(getExpedienteExternalId(item) ?? "") === String(expedienteIdByOrigen))
            : prestacionesCP.find(item => String(getPrestacionId(item) ?? "") === String(data?.TipoGastos?.ExpedienteGasto ?? ""));

        const expedienteIdToSend = info
            ? (getExpedienteExternalId(expedienteSeleccionado) ?? datosAPI?.ExpedienteId ?? null)
            : (datosAPI?.ExpedienteId ?? null);
        const prestacionIdToSend = info
            ? (getPrestacionId(expedienteSeleccionado) ?? datosAPI?.PrestacionId ?? null)
            : (datosAPI?.PrestacionId ?? null);
        const datosOtrosTransportes = await exportGastosOtrosVehiculos(data?.TipoGastos?.ViajesOtrosVehiculos ?? []);

        const datosEnvio = {
            Id: datosAPI?.Id ?? null,
            PersonaId: parseInt(tokenResponse?.extension_external_id ?? "0"),
            UsuarioId: tokenResponse?.extension_id ?? null,
            ImpersonadorId: datosAPI?.ImpersonadorId ?? null,
            Acepto: data?.Acepto ?? false,
            ExpedienteId: expedienteIdToSend,
            PrestacionId: prestacionIdToSend,
            CodigoExpediente: info
                ? (expedienteSeleccionado?.CodigoExpediente ?? null)
                : (datosAPI?.CodigoExpediente ?? null),
            DatosPersonales: {
                Nombre: data?.DatosPersonales?.SolicitanteNombre ?? null,
                Apellido1: data?.DatosPersonales?.SolicitanteApellido1 ?? null,
                Apellido2: data?.DatosPersonales?.SolicitanteApellido2 ?? null,
                IPF: data?.DatosPersonales?.SolicitanteIPF ?? null,
                IPFTipo: parseNumberOrNull(data?.DatosPersonales?.SolicitanteIPFTipo),
                Telefono: data?.DatosPersonales?.SolicitanteTelefono ?? null,
                Fijo: data?.DatosPersonales?.SolicitanteFijo ?? null,
                Domicilio: data?.DatosPersonales?.SolicitanteDomicilio ?? null,
                CodigoPostal: data?.DatosPersonales?.SolicitanteCodigoPostal ?? null,
                ProvinciaDescripcion: data?.DatosPersonales?.SolicitanteProvinciaDescripcion ?? null,
                CodigoProvincia: data?.DatosPersonales?.SolicitanteProvincia ?? null,
                CodigoLocalidad: data?.DatosPersonales?.SolicitanteComunidadCodigo ?? null,
                LocalidadDescripcion: data?.DatosPersonales?.SolicitanteComunidadCodigoDescripcion ?? null,
                NumeroSeguridadSocial: datosAPI?.DatosPersonales?.NumeroSeguridadSocial ?? null,
            },
            Iban: formatIban(data?.DatosBancarios?.SolicitanteIBAN),
            DatosVehiculoPropio: exportCalculadoraKilometraje(data?.TipoGastos?.ViajesVehiculoPropio ?? []),
            DatosOtrosTransportes: datosOtrosTransportes,
        };

        try {
            const userId = tokenResponse?.extension_id ?? datosAPI?.UsuarioId ?? "";

            if (info) {
                await createSolicitudGastosTransporte(userId, datosEnvio, onSuccess);
            } else {
                await updateSolicitudGastosTransporteById(userId, String(datosAPI?.Id ?? ""), datosEnvio, onSuccess);
            }

            setBloquearEnvio(true);
        } catch (error) {
            console.error("Error al enviar SolicitudGastosTransporte:", error);
        } finally {
            setTimeout(() => {
                setLoader(false);
            }, 500);
        }
    };

    return (
        <>
            <Loader activeLoader={loader || loadingCreateSolicitudGastosTransporte || loadingUpdateSolicitudGastosTransporteById} label="Cargando Formulario" />

            {expedienteNoEncontradoError ? (
                <ErrorDisplay descripcionError={expedienteNoEncontradoError} />
            ) : (
                <FormProvider {...formMethods}>
                    <div className="container form--simple-styled" id="formDeltawithoutleave">
                        <FormObservaciones observaciones={datosAPI?.Observaciones} titulo={'Observaciones del Gestor a la solicitud'} errorColor={errorObservaciones} />

                        <FormInfoCreateUpdate
                            isCreate={info}
                            estadoFormulario={datosAPI?.EstadoFormulario ?? datosAPI?.estadoFormulario}
                            estadoFormularioDescripcion={datosAPI?.EstadoFormularioDescripcion ?? datosAPI?.estadoFormularioDescripcion}
                            fechaActualizacionEstado={datosAPI?.FechaActualizacionEstado ?? datosAPI?.fechaActualizacionEstado}
                        />

                        <FormDatosPersonales />

                        <AccordionForm title={'Datos del Gasto'} expandedInit={false} nombreError='TipoGastos' totalSize={totalSize} maxSize={MAX_SIZE_MB} >
                            <div className="row">
                                <div className="col-xl-12">
                                    <FieldsetContainer legend="Prestación relacionada" disabled={false}>
                                        {info ?
                                            (expedienteBloqueadoLabel ?
                                                <>
                                                    <input type='hidden' {...register('TipoGastos.ExpedienteGasto')} />
                                                    <FormInputText
                                                        key={expedienteBloqueadoLabel}
                                                        name='TipoGastos.ExpedienteGastoBloqueado'
                                                        id='TipoGastos.ExpedienteGastoBloqueado'
                                                        label='Prestación relacionada al gasto'
                                                        md={12}
                                                        xl={12}
                                                        disabled={true}
                                                        defaultValue={expedienteBloqueadoLabel}
                                                    />
                                                </>
                                                :
                                                <FormInputDropDown
                                                    name="TipoGastos.ExpedienteGasto"
                                                    label="Prestación relacionada al gasto"
                                                    required="Debe seleccionar una Prestacion"
                                                    options={expedienteArray}
                                                    xl={12}
                                                    md={12}
                                                    col={12}
                                                    placeholder='Selecciona un expediente'
                                                />)
                                            :
                                            <FormInputText
                                                name='TipoGastos.ExpedienteGasto'
                                                id='TipoGastos.ExpedienteGasto'
                                                label='Prestación relacionada al gasto'
                                                md={12}
                                                xl={12}
                                                disabled={true}
                                            />
                                        }

                                    </FieldsetContainer>

                                    <FieldsetContainer legend="Viajes en Vehículo Propio" disabled={false}>
                                        <FormCalculadoraKilometraje
                                            name='TipoGastos.ViajesVehiculoPropio'
                                            id='TipoGastos.ViajesVehiculoPropio'
                                            userId={tokenResponse?.extension_id ?? null}
                                            label='Viajes en vehículo propio'
                                            required={requiredViajesTransporte}
                                            additionalRequiredNames={['TipoGastos.ViajesOtrosVehiculos']}
                                            xl={12}
                                            md={12}
                                            col={12}
                                        />
                                    </FieldsetContainer>

                                    <FieldsetContainer legend="Viajes Otros Vehículos" disabled={false}>
                                        <CalculadoraGastosOtrosVehiculos
                                            name='TipoGastos.ViajesOtrosVehiculos'
                                            id='TipoGastos.ViajesOtrosVehiculos'
                                            label='Viajes otros vehículos'
                                            required={requiredViajesTransporte}
                                            additionalRequiredNames={['TipoGastos.ViajesVehiculoPropio']}
                                            setTotalSize={setTotalSize}
                                            xl={12}
                                            md={12}
                                            col={12}
                                        />
                                    </FieldsetContainer>
                                </div>
                            </div>
                        </AccordionForm>

                        <AccordionForm title={'Datos de Abono'} expandedInit={false} nombreError='DatosBancarios' >
                            <div className="row mb-4">
                                <div className="col-xl-12">
                                    <div className="row mb-4">
                                        <div className="col-xl-12">
                                            <FormInputCuentaBancaria
                                                id='DatosBancarios.SolicitanteIBAN'
                                                name='DatosBancarios.SolicitanteIBAN'
                                                xl={12}
                                                md={12}
                                                placeholder=''
                                                req='Falta el Número de Cuenta Bancaria'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AccordionForm>

                        <FormBotonSubmit
                            disableSubmit={bloquearEnvio}
                            onSubmit={handleSubmit(onSubmit)}
                            successCreate={successCreateSolicitudGastosTransporte}
                            successUpdate={successUpdateSolicitudGastosTransporteById}
                            errorCreate={errorCreateSolicitudGastosTransporte}
                            errorUpdate={errorUpdateSolicitudGastosTransporteById}
                            datosAPI={datosAPI}
                            info={info}
                            totalSizeDocs={totalSize}
                            MaxSizeMB={MAX_SIZE_MB}
                            BloqueoForm={[1, 3, 4, 5]}
                        />

                        <div className='paragraph text-info mt-5'>Solo se abonarán gastos que estén debidamente justificados y autorizados por
                            Solimat Mutua Colaboradora con la Seguridad Social nº 72.</div>
                        <br />
                        <br />
                        <p style={{ fontSize: '12px' }} className='paragraph text-secondary mb-4'>En cumplimiento de lo establecido en la normativa sobre de Protección de Datos de Carácter Personal, Solimat Mutua Colaboradora con la Seguridad Social nº 72 le informa que sus datos de
                            carácter personal serán objeto de tratamiento, con la finalidad del mantenimiento y cumplimiento de la relación. La base jurídica que legitima el tratamiento de sus datos es el
                            cumplimiento de una obligación legal. Sus datos serán conservados de forma indefinida en nuestra entidad para atender a las posibles responsabilidades derivadas del tratamiento. Usted
                            podrá revocar el consentimiento prestado, así como ejercitar sus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento, o portabilidad, en su caso,
                            remitiendo un escrito a la siguiente dirección: Solimat, Mutua Colaboradora con la Seguridad Social nº 72, C/ Berna, 1, 45003, Toledo, adjuntando su NIF. Si considera que sus
                            derechos no han sido debidamente atendidos, puede contactar con nuestro delegado de protección de datos lopd@solimat.com o interponer una reclamación frente a la Agencia
                            Española de Protección de Datos.</p>
                    </div>
                </FormProvider>
            )}
        </>
    );
};

export default SolicitudGastosTransporte;



